import { cn } from "@/lib/utils";
import { useEffect } from "react";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Button, Chip } from "@heroui/react";
import { useDropzone, FileRejection } from "react-dropzone";
import { useTranslation } from "react-i18next";

interface FileUploadProps {
  files: File[];
  setFiles: (files: File[]) => void;
  setError?: (error: string | null) => void;
  acceptedTypes?: string[];
  maxFiles?: number;
  /** Toggle file-type verification (MIME/ext). Defaults to true. */
  verify?: boolean;
  /** Hide the status chip row. Defaults to false (show). */
  hideStatusChips?: boolean;
}

const MIME_TYPE_MAP: Record<string, { mimes: string[]; exts: string[] }> = {
  pdf: { mimes: ["application/pdf"], exts: [".pdf"] },
  excel: {
    mimes: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ],
    exts: [".xlsx", ".xls"],
  },
  image: {
    mimes: ["image/png", "image/jpeg"],
    exts: [".png", ".jpg", ".jpeg"],
  },
  csv: { mimes: ["text/csv"], exts: [".csv"] },
  word: {
    mimes: [
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    exts: [".doc", ".docx"],
  },
};

const getFileLabel = (mimeType: string): string => {
  for (const [label, spec] of Object.entries(MIME_TYPE_MAP)) {
    if (spec.mimes.includes(mimeType)) return label;
  }
  return mimeType.split("/").pop()?.toUpperCase() || "UNKNOWN";
};

function buildAccept(acceptedKeys: string[] | undefined) {
  const keys = acceptedKeys ?? [];
  const mimes = keys.flatMap((k) => MIME_TYPE_MAP[k]?.mimes ?? []);
  const exts = keys.flatMap((k) => MIME_TYPE_MAP[k]?.exts ?? []);
  const accept = mimes.length
    ? Object.fromEntries(mimes.map((m) => [m, exts]))
    : undefined;
  return { accept, mimes, exts };
}

function isAllowed(
  file: File,
  allowedMimes: string[],
  allowedExts: string[],
  verify: boolean,
) {
  if (!verify) return true; // ⬅️ verification disabled: accept all
  if (!allowedMimes.length && !allowedExts.length) return true; // unrestricted
  const byMime = !!file.type && allowedMimes.includes(file.type);
  if (byMime) return true;
  const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
  return allowedExts.includes(ext);
}

export const FileUpload = ({
  files,
  setFiles,
  setError,
  acceptedTypes,
  maxFiles = Infinity,
  verify = true,
  hideStatusChips = false,
}: FileUploadProps) => {
  const { t } = useTranslation();
  const filesSignature = files
    .map((file) => `${file.name}:${file.size}:${file.lastModified}`)
    .join("|");
  // Build accept only if verification is ON
  const {
    accept,
    mimes: acceptedMIMEs,
    exts: acceptedExts,
  } = verify
    ? buildAccept(acceptedTypes)
    : { accept: undefined, mimes: [] as string[], exts: [] as string[] };

  const handleFileChange = (newFiles: File[]) => {
    const invalid: File[] = [];
    const valid = newFiles.filter((f) => {
      const ok = isAllowed(f, acceptedMIMEs, acceptedExts, verify);
      if (!ok) invalid.push(f);
      return ok;
    });

    if (verify) {
      if (invalid.length) {
        setError?.(
          `${t("file_upload.errors.invalid_format", {
            defaultValue: "Invalid format",
          })}: ${invalid.map((f) => f.name).join(", ")}` +
            (acceptedTypes?.length
              ? ""
              : ` (${t("file_upload.errors.select_config", {
                  defaultValue: "select a configuration to restrict the format",
                })})`),
        );
      } else {
        setError?.(null);
      }
    } else {
      // When not verifying, never surface format errors
      setError?.(null);
    }

    // Merge with existing, de-dup by name, then enforce maxFiles
    const all = [...files, ...valid];
    const unique = Array.from(new Map(all.map((f) => [f.name, f])).values());
    const limited = unique.slice(0, maxFiles);

    if (unique.length > maxFiles) {
      setError?.(
        t("file_upload.errors.max_files", {
          defaultValue: "You can select at most {{count}} file(s).",
          count: maxFiles,
        }),
      );
    }

    setFiles(limited);
  };

  // Re-validate current files only when verification is ON and acceptedTypes changes
  useEffect(() => {
    if (!verify || !files.length) return;
    const stillValid = files.filter((f) =>
      isAllowed(f, acceptedMIMEs, acceptedExts, verify),
    );
    if (stillValid.length !== files.length) {
      const removed = files.filter((f) => !stillValid.includes(f));
      setFiles(stillValid);
      setError?.(
        `${t("file_upload.errors.mismatch_config", {
          defaultValue: "These files do not match the selected configuration",
        })}: ${removed.map((f) => f.name).join(", ")}`,
      );
    } else {
      setError?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedTypes, verify]);

  // Popover positioning can be stale right after file cards are added.
  // Triggering a resize event forces floating-position recalculation.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const rafId = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [filesSignature]);

  const handleRemoveFile = (fileName: string) => {
    const updated = files.filter((f) => f.name !== fileName);
    setFiles(updated);
    if (!updated.length) setError?.(null);
  };

  const onDropRejected = (rejections: FileRejection[]) => {
    // Only surface rejections from react-dropzone itself (e.g., maxFiles). Type rejections
    // only occur when "accept" is set, which we disable when verify=false.
    const msgs = rejections.map((r) => {
      const reasons = r.errors.map((e) => e.message).join("; ");
      return `${r.file.name}: ${reasons}`;
    });
    if (msgs.length) setError?.(msgs.join("\n"));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: maxFiles > 1,
    maxFiles,
    // If verify=false, don't pass "accept" so anything is allowed at the dropzone level.
    accept: verify ? accept : undefined,
    onDrop: handleFileChange,
    onDropRejected,
  });

  const acceptAttr =
    verify && accept ? Object.keys(accept).join(",") : undefined;

  return (
    <div
      className="w-full min-h-80 p-10 group/file block rounded-lg cursor-pointer relative overflow-hidden"
      {...getRootProps()}
    >
      <input
        {...getInputProps()}
        id="file-upload-handle"
        type="file"
        accept={acceptAttr}
        multiple={maxFiles > 1}
        onChange={(e) => handleFileChange(Array.from(e.target.files || []))}
        className="hidden"
      />
      <div className="flex flex-col items-center justify-center">
        <p className="relative z-20 font-bold text-neutral-500 dark:text-neutral-300">
          {t("file_upload.drop_here", {
            defaultValue: "Drag & drop",
          })}{" "}
          {t(maxFiles > 1 ? "file_upload.drop_many" : "file_upload.drop_one", {
            defaultValue: maxFiles > 1 ? "one or more files" : "one file",
          })}
        </p>
        <div className="relative w-full mt-10 max-w-xl mx-auto">
          {files.map((file, idx) => (
            <div
              key={"file" + idx}
              className={cn(
                "relative overflow-hidden z-40 bg-white dark:bg-neutral-900 flex flex-col items-start justify-start md:h-24 p-4 mt-4 w-full mx-auto rounded-md gap-3 transition",
                "shadow-sm",
              )}
            >
              <div className="relative flex justify-between w-full items-center">
                <p className="text-sm text-neutral-700 dark:text-neutral-300 truncate max-w-sm">
                  {file.name}
                </p>

                <Button
                  isIconOnly
                  radius="full"
                  size="sm"
                  variant="light"
                  className="absolute top-[-14px] right-[-14px]"
                  onPress={() => handleRemoveFile(file.name)}
                >
                  <XMarkIcon className="h-4 w-4" />
                </Button>
              </div>

              {!hideStatusChips && (
                <div className="flex text-sm flex-row items-center w-full mt-2 justify-between text-neutral-400 dark:text-neutral-400">
                  <p>
                    {(file.size / (1024 * 1024)).toFixed(2)} MB |{" "}
                    {new Date(file.lastModified).toLocaleDateString()}
                  </p>
                  <div>
                    {!verify ? (
                      <Chip color="default" variant="light">
                        {t("file_upload.status.unverified", {
                          defaultValue: "Unverified",
                        })}
                      </Chip>
                    ) : !acceptedMIMEs.length && !acceptedExts.length ? (
                      <Chip color="default" variant="light">
                        {t("file_upload.status.verifying", {
                          defaultValue: "Verifying…",
                        })}
                      </Chip>
                    ) : isAllowed(file, acceptedMIMEs, acceptedExts, verify) ? (
                      <Chip color="success" variant="light">
                        {file.type ? getFileLabel(file.type) : "OK"}
                      </Chip>
                    ) : (
                      <Chip color="danger" variant="bordered">
                        {t("file_upload.status.invalid_format", {
                          defaultValue: "Invalid format",
                        })}
                      </Chip>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {!files.length && (
            <div
              className={cn(
                "relative group-hover/file:shadow-2xl z-40 bg-white dark:bg-neutral-900 flex items-center justify-center h-32 mt-4 w-full max-w-[8rem] mx-auto rounded-md transition",
                "shadow-[0px_10px_50px_rgba(0,0,0,0.1)]",
              )}
            >
              {isDragActive ? (
                <p className="text-neutral-600 flex flex-col items-center">
                  {t("file_upload.drop", { defaultValue: "Drop" })}
                  <ArrowUpTrayIcon className="h-4 w-4 text-neutral-600 dark:text-neutral-400" />
                </p>
              ) : (
                <ArrowUpTrayIcon className="h-4 w-4 text-neutral-600 dark:text-neutral-300" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
